import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  BackLink,
  Button,
  Container,
  Dialog,
  Heading,
  Icon,
  Muted,
  Screen,
  Skeleton,
  Spinner,
  Stack,
  Text,
  useToast,
} from '@fluent/ui';
import { useAuth } from '../auth/authStore.js';
import { useSubscription } from '../subscription/hooks.js';
import { EVERYTHING_FREE } from '../subscription/freeMode.js';
import { derivedStatus, type DerivedStatus } from '../subscription/access.js';
import {
  ManageError,
  useCancelSubscription,
  useResumeSubscription,
  useScheduledRedemption,
  useSubscriptionDetails,
  type CanonicalRow,
  type PaymentMethodSummary,
  type ScheduledRedemption,
  type TransactionSummary,
} from '../subscription/manage.js';
import { PaymentFrame, type FrameMode } from '../subscription/PaymentFrame.js';
import { isNativeApp } from '../native/platform.js';
import { LockGlyph, CardGlyph, CheckCircleGlyph, CoinGlyph } from '../profile/icons.js';
import { formatDateLong, formatDateShort, toDateLang, type DateLang } from '../lib/dates.js';
import { formatUsd, nextChargeCents } from '../subscription/price.js';
import { coinsToGel } from '../missions/coinValue.js';
import './Subscription.css';

/**
 * /subscription — native subscription management (trust-first design,
 * DECISIONS 2026-07-17, supersedes 2026-05-04 "portal-only").
 *
 * The whole surface is ours: status, dates, price, card-on-file, payment
 * history, cancel/resume, and card entry (Paddle's PCI iframe embedded
 * inline in a container we style — the only part of the page we cannot
 * paint, honestly declared in the trust footer).
 *
 * TRUST CONTRACT (the 2026-05-04 postmortem, inverted into architecture):
 *   - No optimistic state. After any mutation the UI renders ONLY the
 *     canonical row the server re-read from Paddle.
 *   - A failed VERIFY is rendered as the amber "change in progress"
 *     (unknown) card — never as success, never as "still active".
 *   - A confirmed no-change renders an honest "nothing changed" error.
 */

export function Subscription() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [params] = useSearchParams();

  // Cached DB row renders instantly; the details query brings live truth.
  const { data: cachedRow } = useSubscription();
  const details = useSubscriptionDetails();
  const { data: scheduledRedemption } = useScheduledRedemption();
  const cancelMut = useCancelSubscription();
  const resumeMut = useResumeSubscription();

  const [dialog, setDialog] = useState<'cancel' | 'resume' | null>(null);
  const [dialogPhase, setDialogPhase] = useState<'confirm' | 'pending' | 'error'>('confirm');
  const [unknown, setUnknown] = useState(false);
  const unknownSinceRef = useRef(0);
  const [noChange, setNoChange] = useState(false);
  const [frameMode, setFrameMode] = useState<FrameMode | null>(null);
  const [awaitingWebhook, setAwaitingWebhook] = useState(false);
  const [celebrate, setCelebrate] = useState(false);

  // The current_subscription view has nullable columns; normalize the
  // cached row into the canonical shape for the instant first paint.
  const cachedCanonical: CanonicalRow | null = cachedRow?.status
    ? {
        status: cachedRow.status,
        trial_ends_at: cachedRow.trial_ends_at,
        current_period_starts_at: cachedRow.current_period_starts_at,
        current_period_ends_at: cachedRow.current_period_ends_at,
        cancelled_at: cachedRow.cancelled_at,
        provider_price_id: cachedRow.provider_price_id,
      }
    : null;
  const row: CanonicalRow | null = details.data ? details.data.row : cachedCanonical;
  const status: DerivedStatus = derivedStatus(
    row as Parameters<typeof derivedStatus>[0],
  );
  const firstLoad = details.isLoading && !cachedRow;

  /* -------- deep link: /subscription?fix=1 opens the fix-card frame -------- */
  // Paddle's own payment links (Retain's failed-payment emails) land here as
  // ?_ptxn=txn_… — the Default payment link in Paddle's checkout settings.
  // Same intent: open the fix-card frame (it mints its own transaction).
  const fixWanted = params.get('fix') === '1' || params.has('_ptxn');
  const fixOpenedRef = useRef(false);
  useEffect(() => {
    if (fixWanted && status === 'past_due' && !fixOpenedRef.current) {
      fixOpenedRef.current = true;
      setFrameMode('fix');
    }
  }, [fixWanted, status]);

  /* -------- background re-poll while the unknown card is up -------- */
  useEffect(() => {
    if (!unknown) return;
    unknownSinceRef.current = Date.now();
    const timers = [1500, 3000, 5000].map((ms) =>
      window.setTimeout(() => void details.refetch(), ms),
    );
    return () => timers.forEach((id) => window.clearTimeout(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch identity is stable enough; re-arming on every details change would loop
  }, [unknown]);

  // The amber banner exits ONLY when a live read newer than the ambiguity
  // succeeds — never optimistically on button press (a failed recheck must
  // not quietly render possibly-stale state as if it were confirmed).
  useEffect(() => {
    if (unknown && details.isSuccess && details.dataUpdatedAt > unknownSinceRef.current) {
      setUnknown(false);
    }
  }, [unknown, details.isSuccess, details.dataUpdatedAt]);

  /* -------- post-checkout: poll until the webhook lands -------- */
  const pollTicksRef = useRef(0);
  useEffect(() => {
    if (!awaitingWebhook) return;
    pollTicksRef.current = 0;
    const id = window.setInterval(() => {
      pollTicksRef.current += 1;
      void details.refetch();
      if (pollTicksRef.current >= 10) {
        window.clearInterval(id);
        setAwaitingWebhook(false);
        setUnknown(true);
      }
    }, 2000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- armed only on the awaitingWebhook edge
  }, [awaitingWebhook]);

  // Success edge: webhook landed, live row shows entitlement. No
  // transition requirement — if the status is ALREADY entitled when we
  // start waiting (fast webhook, double checkout), that is success, not
  // a 20s stall into a false amber banner.
  useEffect(() => {
    if (!awaitingWebhook) return;
    if (status === 'trialing' || status === 'active') {
      setAwaitingWebhook(false);
      setFrameMode(null);
      setCelebrate(true);
      toast({ tone: 'success', title: t('sub_page.toast.subscribed') });
      window.setTimeout(() => setCelebrate(false), 400);
    }
  }, [status, awaitingWebhook, toast, t]);

  /* -------- mutations with honest outcomes -------- */
  const runMutation = (which: 'cancel' | 'resume') => {
    const mut = which === 'cancel' ? cancelMut : resumeMut;
    setDialogPhase('pending');
    setNoChange(false);
    mut.mutate(undefined, {
      onSuccess: (body) => {
        setDialog(null);
        setDialogPhase('confirm');
        const date = fmtLong(
          body.row.current_period_ends_at ?? body.row.trial_ends_at,
          toDateLang(i18n.language),
        );
        toast({
          tone: 'success',
          title:
            which === 'cancel'
              ? t('sub_page.toast.cancelled', { date })
              : t('sub_page.toast.resumed', { date }),
        });
      },
      onError: (e) => {
        if (e instanceof ManageError && e.code === 'paddle_verify_failed') {
          // Write may have landed; we refuse to guess. Amber card takes over.
          setDialog(null);
          setDialogPhase('confirm');
          setUnknown(true);
          return;
        }
        if (
          e instanceof ManageError &&
          (e.code === 'cancel_not_confirmed' || e.code === 'resume_not_confirmed')
        ) {
          setNoChange(true);
        }
        setDialogPhase('error');
      },
    });
  };

  if (!user) return null;

  // Everything is free (freeMode.ts): nothing to sell here, so a learner
  // with no live subscription goes home. One that is still live keeps the
  // page, so its owner can see it and cancel.
  if (EVERYTHING_FREE && !firstLoad && (status === 'none' || status === 'expired')) {
    return <Navigate to="/" replace />;
  }

  return (
    <SubscriptionView
      status={status}
      row={row}
      paymentMethod={details.data?.payment_method ?? null}
      transactions={details.data?.transactions ?? null}
      scheduledRedemption={scheduledRedemption ?? null}
      firstLoad={firstLoad}
      loadFailed={details.isError}
      onRetryLoad={() => void details.refetch()}
      confirming={awaitingWebhook}
      celebrate={celebrate}
      unknown={unknown}
      onRecheck={() => void details.refetch()}
      frame={
        frameMode && (
          <PaymentFrame
            mode={frameMode}
            email={user.email ?? ''}
            userId={user.id}
            trialOrPeriodEnd={row?.trial_ends_at ?? row?.current_period_ends_at ?? null}
            onClose={() => setFrameMode(null)}
            onCompleted={() => {
              if (frameMode === 'update' || frameMode === 'fix') {
                setFrameMode(null);
                toast({ tone: 'success', title: t('sub_page.toast.card_updated') });
                void details.refetch();
              } else {
                setAwaitingWebhook(true);
              }
            }}
          />
        )
      }
      frameOpen={frameMode !== null}
      onBack={() => navigate('/profile')}
      onSubscribe={() => setFrameMode('subscribe')}
      onResubscribe={() => setFrameMode('resubscribe')}
      native={isNativeApp()}
      onFixCard={() => setFrameMode('fix')}
      onChangeCard={() => setFrameMode('update')}
      onCancel={() => {
        setDialogPhase('confirm');
        setDialog('cancel');
      }}
      onResume={() => {
        setDialogPhase('confirm');
        setDialog('resume');
      }}
      dialog={
        <MutationDialog
          which={dialog}
          phase={dialogPhase}
          noChange={noChange}
          dateIso={row?.current_period_ends_at ?? null}
          onConfirm={() => dialog && runMutation(dialog)}
          onClose={() => {
            setDialog(null);
            setDialogPhase('confirm');
            if (noChange) void details.refetch();
          }}
        />
      }
    />
  );
}

/* ====================================================================== */
/*  Presentational view — /subscription-review mounts this with mocks.    */
/* ====================================================================== */

export interface SubscriptionViewProps {
  status: DerivedStatus;
  row: CanonicalRow | null;
  paymentMethod: PaymentMethodSummary | null;
  transactions: TransactionSummary[] | null;
  scheduledRedemption?: ScheduledRedemption | null;
  firstLoad: boolean;
  loadFailed?: boolean;
  onRetryLoad?: () => void;
  confirming: boolean;
  celebrate?: boolean;
  unknown: boolean;
  onRecheck: () => void;
  frame?: ReactNode;
  frameOpen: boolean;
  onBack: () => void;
  onSubscribe: () => void;
  onResubscribe: () => void;
  /** Store app: status only — no price, no checkout, no card, no cancel,
   *  no history, no payment-processor footer (DECISIONS 2026-09-21). */
  native?: boolean;
  onFixCard: () => void;
  onChangeCard: () => void;
  onCancel: () => void;
  onResume: () => void;
  dialog?: ReactNode;
}

/**
 * Deep link into Google's own subscription settings for this app.
 *
 * Everything that changes a Play subscription — cancel, resume, change card,
 * see receipts — happens here and not in our UI. That is Google's rule and it
 * is also the right shape: they hold the card, so a cancel button of ours
 * would be a button that cannot be trusted to work.
 */
const PLAY_MANAGE_URL =
  'https://play.google.com/store/account/subscriptions?sku=fluentge_pro_monthly&package=ge.fluent.app';

export function SubscriptionView(props: SubscriptionViewProps) {
  const { t, i18n } = useTranslation();
  const lang = toDateLang(i18n.language);
  const {
    status,
    row,
    paymentMethod,
    transactions,
    firstLoad,
    confirming,
    unknown,
    frameOpen,
  } = props;

  /** Google bills this one, so Google manages it. */
  const isPlay = row?.provider === 'google_play';
  /** Pro given free (migration 0023): no charge date, card, history or
   *  cancel — there is nothing to pay and nothing to stop. */
  const isGift = row?.provider === 'gift';
  /** Store app: show what they have, never how to pay. */
  const native = props.native === true;
  const openPlayManage = () => window.open(PLAY_MANAGE_URL, '_blank', 'noopener');

  const dateLabel =
    status === 'trialing'
      ? t('sub_page.date_label.first_charge')
      : status === 'cancelling'
        ? t('sub_page.date_label.access_until')
        : status === 'expired'
          ? t('sub_page.date_label.ended')
          : t('sub_page.date_label.next_charge');
  const dateIso =
    status === 'trialing'
      ? (row?.trial_ends_at ?? row?.current_period_ends_at)
      : row?.current_period_ends_at;
  const note = isGift
    ? t('sub_page.note.gift')
    : status === 'trialing' || status === 'cancelling' || status === 'past_due' || status === 'expired'
      ? t(`sub_page.note.${status}`)
      : null;

  return (
    <Screen>
      <Container size="sm">
        <div className="fluent-sub">
          <BackLink label={t('common.back')} onPress={props.onBack} />

          <Heading level={1}>{t('sub_page.title')}</Heading>

          {/* ---------- unknown (change-in-progress) banner ---------- */}
          {unknown && (
            <section className="fluent-sub__unknown" role="status">
              <Spinner size={20} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <Text weight="bold" style={{ display: 'block' }}>
                  {t('sub_page.unknown.title')}
                </Text>
                <Text tone="muted" size="sm" style={{ display: 'block' }}>
                  {t('sub_page.unknown.body')}
                </Text>
              </div>
              <Button variant="neutral" size="md" onClick={props.onRecheck}>
                {t('sub_page.unknown.recheck')}
              </Button>
            </section>
          )}

          {firstLoad ? (
            <StatusSkeleton />
          ) : props.loadFailed && !row ? (
            <section className="fluent-sub__card">
              <Stack gap={3} align="center" style={{ textAlign: 'center' }}>
                <Text tone="muted">{t('sub_page.error_load')}</Text>
                <Button variant="secondary" size="md" onClick={props.onRetryLoad}>
                  {t('sub_page.retry')}
                </Button>
              </Stack>
            </section>
          ) : status === 'none' && native ? (
            <section className="fluent-sub__card" data-status="none">
              <Text tone="muted">{t('sub_page.native_free')}</Text>
            </section>
          ) : status === 'none' ? (
            <PitchCard onSubscribe={props.onSubscribe} frameOpen={frameOpen} />
          ) : (
            <section
              className="fluent-sub__card"
              data-status={status}
              data-confirming={confirming || undefined}
            >
              {confirming && (
                <div className="fluent-sub__confirming" role="status">
                  <Spinner size={18} />
                  <Text size="sm" weight="bold">
                    {t('sub_page.pending')}
                  </Text>
                </div>
              )}
              <div className="fluent-sub__chip-row">
                <span
                  className="fluent-sub__chip"
                  data-status={status}
                  data-celebrate={props.celebrate || undefined}
                >
                  {t(`sub_page.chip.${status}`)}
                </span>
              </div>
              <Text tone="muted" size="sm" weight="bold">
                {t(isGift ? 'sub_page.plan_line_gift' : native ? 'sub_page.plan_line_native' : 'sub_page.plan_line')}
              </Text>
              {!isGift && <div className="fluent-sub__date">
                <span className="fluent-sub__date-label">{dateLabel}</span>
                <span className="fluent-sub__date-value">
                  {/* With coins scheduled, say what will actually be charged.
                      The old layout printed $5 here and the discount two lines
                      down, leaving the learner to subtract (audit F089). */}
                  {props.scheduledRedemption && (status === 'active' || status === 'trialing')
                    ? t('sub_page.date_value_with_amount', {
                        date: fmtLong(dateIso ?? null, lang),
                        amount: formatUsd(nextChargeCents(props.scheduledRedemption.usd_cents)),
                      })
                    : fmtLong(dateIso ?? null, lang)}
                </span>
              </div>}
              {note && !native && (
                <Text tone="muted" size="sm">
                  {note}
                </Text>
              )}

              {/* pending coin discount, scheduled by the redemption engine */}
              {!native && !isGift && props.scheduledRedemption && (status === 'active' || status === 'trialing') && (
                <div className="fluent-sub__coins-line">
                  <span className="fluent-sub__coins-line-icon" aria-hidden="true">
                    <Icon size={16}>
                      <CoinGlyph />
                    </Icon>
                  </span>
                  <Text size="sm" weight="bold">
                    {t('sub_page.coins_discount', {
                      coins: props.scheduledRedemption.coins,
                      gel: coinsToGel(props.scheduledRedemption.coins),
                      usd: (props.scheduledRedemption.usd_cents / 100).toFixed(2),
                    })}
                  </Text>
                </div>
              )}

              {/* card on file — Paddle only. Google holds the card for Play
                  subscriptions and never tells us anything about it. */}
              {!native && !isPlay && !isGift && paymentMethod && (
                <div className="fluent-sub__cardrow">
                  <span className="fluent-sub__cardrow-icon" aria-hidden="true">
                    <Icon size={18}>
                      <CardGlyph />
                    </Icon>
                  </span>
                  <Text weight="bold" size="sm" style={{ flex: 1 }}>
                    {t('sub_page.card.value', {
                      brand: brandLabel(paymentMethod.brand),
                      last4: paymentMethod.last4,
                    })}
                  </Text>
                  {(status === 'trialing' || status === 'active') && (
                    <Button variant="ghost" size="md" onClick={props.onChangeCard}>
                      {t('sub_page.card.change')}
                    </Button>
                  )}
                </div>
              )}

              {/* state-primary action.
                  On Play, resume / fix-card / cancel are all the same
                  destination: Google's subscription settings. Offering our own
                  buttons would promise actions we cannot perform. Re-subscribing
                  is the exception — that is a NEW purchase, which we do handle. */}
              {native || isGift ? null : isPlay ? (
                <>
                  {status === 'expired' ? (
                    <Button variant="primary" size="lg" fullWidth onClick={props.onResubscribe}>
                      {t('sub_page.cta.resubscribe')}
                    </Button>
                  ) : (
                    <Button
                      variant={status === 'past_due' ? 'danger' : 'primary'}
                      size="lg"
                      fullWidth
                      onClick={openPlayManage}
                    >
                      {t('sub_page.cta.manage_in_play')}
                    </Button>
                  )}
                </>
              ) : (
                <>
                  {status === 'cancelling' && (
                    <Button variant="primary" size="lg" fullWidth onClick={props.onResume}>
                      {t('sub_page.cta.resume')}
                    </Button>
                  )}
                  {status === 'past_due' && (
                    <Button variant="danger" size="lg" fullWidth onClick={props.onFixCard}>
                      {t('sub_page.cta.fix_card')}
                    </Button>
                  )}
                  {status === 'expired' && (
                    <Button variant="primary" size="lg" fullWidth onClick={props.onResubscribe}>
                      {t('sub_page.cta.resubscribe')}
                    </Button>
                  )}
                </>
              )}
            </section>
          )}

          {/* ---------- inline card entry ---------- */}
          {!native && props.frame}

          {/* ---------- payment history ----------
              Play receipts live in Google's account settings; we never see
              them, so we do not pretend to have a history section. */}
          {!native && !isPlay && !isGift && status !== 'none' && !firstLoad && (
            <HistorySection transactions={transactions} />
          )}

          {/* ---------- quiet cancel ----------
              Not rendered for Play: the manage button above already goes to
              the only place a Play subscription can be cancelled. */}
          {!native && !isPlay && !isGift && (status === 'trialing' || status === 'active' || status === 'past_due') && (
            <Button variant="ghost" size="md" fullWidth onClick={props.onCancel} className="fluent-sub__cancel">
              {t('sub_page.cta.cancel')}
            </Button>
          )}

          {/* ---------- trust footer ----------
              Not in the store app: it names the payment processor. */}
          {!native && !isGift && <div className="fluent-sub__trust">
            <span aria-hidden="true" className="fluent-sub__trust-icon">
              <Icon size={16}>
                <LockGlyph />
              </Icon>
            </span>
            <Muted>{t(isPlay ? 'sub_page.trust_play' : 'sub_page.trust')}</Muted>
          </div>}
        </div>
      </Container>
      {props.dialog}
    </Screen>
  );
}

/* ---------------------------------------------------------------------- */

function PitchCard({ onSubscribe, frameOpen }: { onSubscribe: () => void; frameOpen: boolean }) {
  const { t } = useTranslation();
  const perks = ['all_lessons', 'audio', 'streak_freeze', 'cancel_anytime'] as const;
  return (
    <section className="fluent-sub__card" data-status="none">
      <Heading level={3}>{t('sub_page.pitch.title')}</Heading>
      <ul className="fluent-sub__perks">
        {perks.map((p) => (
          <li key={p}>
            <span className="fluent-sub__perk-check" aria-hidden="true">
              <Icon size={18}>
                <CheckCircleGlyph />
              </Icon>
            </span>
            <Text size="sm">{t(`paywall.perk.${p}`)}</Text>
          </li>
        ))}
      </ul>
      <div className="fluent-sub__date">
        <span className="fluent-sub__date-label">{t('sub_page.pitch.price_line')}</span>
        {/* Priced in dollars, paid from a lari account (audit F007). */}
        <span className="fluent-sub__date-approx">{t('paywall.approx_gel')}</span>
      </div>
      {!frameOpen && (
        <Button variant="primary" size="lg" fullWidth onClick={onSubscribe}>
          {t('sub_page.pitch.cta')}
        </Button>
      )}
    </section>
  );
}

function HistorySection({ transactions }: { transactions: TransactionSummary[] | null }) {
  const { t, i18n } = useTranslation();
  const lang = toDateLang(i18n.language);
  const visible = (transactions ?? []).filter((tx) =>
    ['completed', 'paid', 'billed', 'past_due'].includes(tx.status),
  );
  return (
    <section className="fluent-sub__history">
      <Heading level={4} as="h2">
        {t('sub_page.history.title')}
      </Heading>
      {transactions === null ? (
        <div className="fluent-sub__history-card">
          {[0, 1, 2].map((i) => (
            <div key={i} className="fluent-sub__history-row">
              <Skeleton width="40%" height={16} />
              <Skeleton width="20%" height={16} />
            </div>
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div className="fluent-sub__history-card">
          <div className="fluent-sub__history-row">
            <Muted>{t('sub_page.history.empty')}</Muted>
          </div>
        </div>
      ) : (
        <div className="fluent-sub__history-card">
          {visible.map((tx) => (
            <div key={tx.id} className="fluent-sub__history-row">
              <Text weight="bold" size="sm">
                {formatDateShort(tx.billed_at, lang)}
              </Text>
              <span className="fluent-sub__history-right">
                <Text weight="bold" size="sm">
                  {fmtMoney(tx.total, tx.currency)}
                </Text>
                {tx.status === 'past_due' && (
                  <span className="fluent-sub__pill" data-tone="danger">
                    {t('sub_page.history.failed')}
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function StatusSkeleton() {
  return (
    <section className="fluent-sub__card" aria-busy="true">
      <Skeleton width={120} height={28} />
      <Skeleton width="55%" height={14} />
      <div className="fluent-sub__date">
        <Skeleton width="35%" height={12} />
        <Skeleton width="70%" height={28} />
      </div>
      <Skeleton shape="block" width="100%" height={48} />
    </section>
  );
}

/* ---------------------------------------------------------------------- */

interface MutationDialogProps {
  which: 'cancel' | 'resume' | null;
  phase: 'confirm' | 'pending' | 'error';
  noChange: boolean;
  dateIso: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

function MutationDialog({ which, phase, noChange, dateIso, onConfirm, onClose }: MutationDialogProps) {
  const { t, i18n } = useTranslation();
  if (!which) return null;
  const lang = toDateLang(i18n.language);
  const date = fmtLong(dateIso, lang);
  const ns = which === 'cancel' ? 'sub_page.cancel_dialog' : 'sub_page.resume_dialog';

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && phase !== 'pending') onClose();
      }}
      dismissible={phase !== 'pending'}
      title={
        phase === 'error'
          ? t('sub_page.error_dialog.title')
          : t(`${ns}.title`)
      }
      description={
        phase === 'pending'
          ? t('sub_page.pending')
          : phase === 'error'
            ? `${noChange ? `${t('sub_page.no_change')} ` : ''}${t('sub_page.error_dialog.body')}`
            : t(`${ns}.body`, { date })
      }
      actions={
        phase === 'pending' ? (
          <Spinner size={22} />
        ) : phase === 'error' ? (
          <>
            <Button variant="neutral" size="md" onClick={onClose}>
              {t('sub_page.error_dialog.close')}
            </Button>
            <Button variant="secondary" size="md" onClick={onConfirm}>
              {t('sub_page.error_dialog.retry')}
            </Button>
          </>
        ) : which === 'cancel' ? (
          <>
            <Button variant="neutral" size="md" onClick={onClose} autoFocus>
              {t('sub_page.cancel_dialog.keep')}
            </Button>
            <Button variant="danger" size="md" onClick={onConfirm}>
              {t('sub_page.cancel_dialog.confirm')}
            </Button>
          </>
        ) : (
          <>
            <Button variant="neutral" size="md" onClick={onClose}>
              {t('sub_page.resume_dialog.back')}
            </Button>
            <Button variant="primary" size="md" onClick={onConfirm}>
              {t('sub_page.resume_dialog.confirm')}
            </Button>
          </>
        )
      }
    />
  );
}

/* ---------------------------------------------------------------------- */

const fmtLong = (iso: string | null, lang: DateLang): string => formatDateLong(iso, lang);

function fmtMoney(lowestDenom: string, currency: string): string {
  const n = Number(lowestDenom);
  if (!Number.isFinite(n)) return '—';
  const amount = (n / 100).toFixed(2);
  if (currency === 'USD') return `$${amount}`;
  if (currency === 'EUR') return `€${amount}`;
  return `${amount} ${currency}`;
}

function brandLabel(brand: string): string {
  const map: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    american_express: 'Amex',
    unionpay: 'UnionPay',
    maestro: 'Maestro',
    discover: 'Discover',
    jcb: 'JCB',
  };
  return map[brand] ?? brand;
}
